import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface AssetInfo {
  ticker: string;
  company_name: string | null;
  sector: string | null;
  industry: string | null;
  market_cap: number | null;
  description: string | null;
  website: string | null;
  logo_url: string | null;
  currency: string | null;
  exchange: string | null;
  yahoo_symbol: string | null;
}

async function fetchAssetInfo(ticker: string): Promise<AssetInfo | null> {
  // Try cache first
  const { data: cached } = await supabase
    .from("asset_info")
    .select("*")
    .eq("ticker", ticker.toUpperCase())
    .single();

  if (cached) return cached as AssetInfo;

  // Call edge function to fetch & cache
  const resp = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/asset-info`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
      },
      body: JSON.stringify({ ticker }),
    }
  );

  if (!resp.ok) return null;
  return resp.json();
}

export function useAssetInfo(ticker: string | null) {
  return useQuery({
    queryKey: ["asset-info", ticker],
    queryFn: () => fetchAssetInfo(ticker!),
    enabled: !!ticker,
    staleTime: 7 * 24 * 60 * 60 * 1000, // 7 days
  });
}
